import React, { useState, useEffect } from 'react';
import MainSidebar from '../../components/ui/MainSidebar';
import { NavigationHeader } from '../../components/ui/NavigationHeader';
import { useAuth } from '../../contexts/AuthContext';
import { auditService } from '../../services/auditService';
import { AuditActivity, AuditFilters, ActivityStats } from './types';
import { ActivityTable } from './components/ActivityTable';
import { FilterSidebar } from './components/FilterSidebar';
import { ActivityDetailsModal } from './components/ActivityDetailsModal';
import { StatsCards } from './components/StatsCards';
import { Button } from '../../components/ui/Button';

const QuoteActivityAuditDashboard: React.FC = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activities, setActivities] = useState<AuditActivity[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState<AuditActivity | null>(null);
  const [filters, setFilters] = useState<AuditFilters>({
    actionType: 'ALL'
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadActivities();
    loadStats();
  }, [filters]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await auditService.getAuditActivities(filters);
      setActivities(data);
    } catch (error) {
      console.error('Error loading audit activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const statsData = await auditService.getActivityStats(filters.startDate, filters.endDate);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading activity stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleExportToCsv = async () => {
    try {
      setExporting(true);
      const csvData = await auditService.exportToCsv(filters);
      
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `audit_trail_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting audit data:', error);
      alert('Failed to export audit data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({ actionType: 'ALL' });
  };

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}`}>
        <NavigationHeader />

        <main className="flex-1 overflow-auto">
          <div className="max-w-[1920px] mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-text-primary mb-2">
                  Quote Activity Audit Dashboard
                </h1>
                <p className="text-text-secondary">
                  Comprehensive compliance tracking and operational oversight of all quote-related activities
                </p>
              </div>
              <Button
                onClick={handleExportToCsv}
                disabled={exporting || activities.length === 0}
                variant="primary"
              >
                {exporting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export to CSV
                  </>
                )}
              </Button>
            </div>

            {/* Statistics Cards */}
            <StatsCards stats={stats} loading={statsLoading} />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Filter Sidebar */}
              <div className="lg:col-span-1">
                <FilterSidebar
                  filters={filters}
                  onFiltersChange={setFilters}
                  onClearFilters={handleClearFilters}
                />
              </div>

              {/* Activity Table */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">
                      Activity Log
                      {activities.length > 0 && (
                        <span className="ml-2 text-sm text-gray-500">
                          ({activities.length} {activities.length === 1 ? 'activity' : 'activities'})
                        </span>
                      )}
                    </h2>
                  </div>
                  <ActivityTable
                    activities={activities}
                    loading={loading}
                    onViewDetails={setSelectedActivity}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Activity Details Modal */}
      <ActivityDetailsModal
        activity={selectedActivity}
        onClose={() => setSelectedActivity(null)}
      />
    </div>
  );
};

export default QuoteActivityAuditDashboard;